import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';

const EMPTY = {
  code: '',
  name: '',
  email: '',
  creditLimit: '0',
  currency: 'CHF',
  priceListId: '',
};

export function CustomerCreateModal({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  // Suggest the next free customer code (K-###) when the modal opens. The field
  // stays editable, and leaving it empty lets the server assign the code anyway.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    salesApi
      .nextCustomerCode()
      .then(({ items }) => {
        if (active && items?.code) {
          setForm((prev) => (prev.code ? prev : { ...prev, code: items.code }));
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [open]);

  const priceLists = useQuery({
    queryKey: ['sales', 'price-lists', 'list'],
    queryFn: () => salesApi.listPriceLists().then((r) => r.items || []),
    enabled: open,
  });
  const activeLists = (priceLists.data || []).filter((pl) => pl.isActive);

  const create = useMutation({
    mutationFn: (body) => salesApi.createCustomer(body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'customers'] });
      setForm(EMPTY);
      setError(null);
      if (onCreated) {
        onCreated(created);
      }
      onClose();
    },
    onError: (err) => {
      if (err?.fields?.length) {
        setError(err.fields.map((f) => f.message).join(' · '));
      } else {
        setError(err?.message || t('common.loadError'));
      }
    },
  });

  function handleField(key) {
    return (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      email: form.email.trim() || undefined,
      creditLimit: form.creditLimit || '0',
      currency: form.currency || 'EUR',
      ...(form.priceListId ? { priceListId: form.priceListId } : {}),
    };
    create.mutate(payload);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('sales.customers.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="modal-body" style={{ padding: 0, gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('col.code')} *</span>
            <input className="input" value={form.code} onChange={handleField('code')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('sales.customers.modal.currency')}</span>
            <input
              className="input"
              value={form.currency}
              onChange={handleField('currency')}
              maxLength={3}
              required
            />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('col.name')} *</span>
          <input className="input" value={form.name} onChange={handleField('name')} required />
        </label>
        <label className="field">
          <span className="field-label">{t('col.email')}</span>
          <input className="input" type="email" value={form.email} onChange={handleField('email')} />
        </label>
        <label className="field">
          <span className="field-label">{t('sales.customers.modal.creditLimit')}</span>
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={form.creditLimit}
            onChange={handleField('creditLimit')}
          />
        </label>
        <label className="field">
          <span className="field-label">{t('priceList.assign')}</span>
          <select className="select" value={form.priceListId} onChange={handleField('priceListId')}>
            <option value="">{t('priceList.none')}</option>
            {activeLists.map((pl) => (
              <option key={pl.id} value={pl.id}>{pl.name}</option>
            ))}
          </select>
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
